<script setup>
import { ref, computed, watch } from 'vue'
import { useLangStore } from '@/stores/lang'
import { useRoute } from 'vue-router'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useRegistrationStore } from '@/stores/registration'
import { useTranslaterStore } from '@/stores/translater'

import checkValid       from '@/helpers/checkValid'
import UTASmartCaptcha  from '@/components/auth/UTASmartCaptcha.vue'
import UTAPassIndicator from '@/components//UTAPassIndicator.vue'

import personalDataAgree from '@/assets/docs/UAZ_end_user_license_agreement.pdf'

const route = useRoute()
const router = useRouter()

const langStore         = useLangStore()
const registrationStore = useRegistrationStore()
const authStore         = useAuthStore()
const translaterStore   = useTranslaterStore()

const lang                      = computed(() => langStore.lang)
const registrateUserProcess     = computed(() => registrationStore.registrateUserProcess)
const registrateUserMsg         = computed(() => registrationStore.registrateUserMsg)
const registrateVerifyProcess   = computed(() => registrationStore.registrateVerifyProcess)
const registrateVerifyMsg       = computed(() => registrationStore.registrateVerifyMsg)
const smartCaptchaId            = computed(() => authStore.smartCaptchaId)

const setRegistrateUserMsg = registrationStore.setRegistrateUserMsg

const getTranslate = translaterStore.getTranslate

const UTARegistrationName       = ref('')
const UTARegistrationLastname   = ref('')
const UTARegistrationSurname    = ref('')
const UTARegistrationEmail      = ref('')
const UTARegistrationPhone      = ref('')
const UTARegistrationPhoneCode  = ref('+7')
const UTARegistrationPass       = ref('')
const UTARegistrationAgree      = ref(false)

const verifyMode        = ref(false)
const isFirstTry        = ref(false)
const scriptErrors      = ref([])
const formErrors        = ref([])
const formInvalids      = ref({})
const registrationData  = ref({})
const showPassword      = ref(false)

// Ошибки скрипта каптчи
function setScriptError(error) {

    scriptErrors.value.push(error)
}

function submitRegistration() {

    formErrors.value = []
    formInvalids.value = {}

    registrationData.value = {
        code: lang.value, 
        user: {} 
    }

    if(UTARegistrationName.value) {
        registrationData.value.user.name = UTARegistrationName.value
    } else {
        formErrors.value.push(getTranslate.REGISTRATION_NAME)
        formInvalids.value.UTARegistrationName = true
    }

    if(UTARegistrationLastname.value) {
        registrationData.value.user.lastName = UTARegistrationLastname.value
    } else {
        formErrors.value.push(getTranslate.REGISTRATION_LASTNAME)
        formInvalids.value.UTARegistrationLastname = true
    }

    if(UTARegistrationSurname.value) {
        registrationData.value.user.surname = UTARegistrationSurname.value
    }

    if(UTARegistrationEmail.value) {
        registrationData.value.user.email = UTARegistrationEmail.value
    } else {
        formErrors.value.push(getTranslate.REGISTRATION_LOGIN)
        formInvalids.value.UTARegistrationEmail = true
    }

    if(UTARegistrationPhone.value && checkValid(UTARegistrationPhone.value, 'phone')) {
        let phone = UTARegistrationPhone.value.match(/[0-9]/g).join('')
        if(UTARegistrationPhone.value[0] == '+') {
            UTARegistrationPhone.value = phone.substring(1)
        }
        registrationData.value.user.phone = UTARegistrationPhoneCode.value + UTARegistrationPhone.value
    } else {
        formErrors.value.push(getTranslate.REGISTRATION_PHONE)
        formInvalids.value.UTARegistrationPhone = true
    }

    if(UTARegistrationPass.value) {
        registrationData.value.user.password = UTARegistrationPass.value
    } else {
        formErrors.value.push(getTranslate.REGISTRATION_PASS)
        formInvalids.value.UTARegistrationPass = true
    }

    if(!UTARegistrationAgree.value) {
        formErrors.value.push(getTranslate.REGISTRATION_AGREE)
        formInvalids.value.UTAAuthAgree = true
    }

    if(!window.smartCaptcha) {
        formErrors.value.push(getTranslate.SMART_CAPTCHA_ERROR_METHOD)
        formInvalids.value.UTAAuthCaptcha = true
    }

    if(formErrors.value.length == 0) {

        if(isFirstTry.value) { 
            window.smartCaptcha.reset(smartCaptchaId.value)
        }

        window.smartCaptcha.execute(smartCaptchaId.value)
    }
}

function captchaValidate(smartCaptchaToken) {

    if(typeof smartCaptchaToken === "string" && smartCaptchaToken.length > 0) {
        registrationData.value.captcha = {
            yandex: {
                token: smartCaptchaToken
            }
        }
    } else {
        formErrors.value.push(getTranslate.REGISTRATION_CAPTCHA)
        formInvalids.value.UTAAuthCaptcha = true
    }

    if(formErrors.value.length == 0) {
        registrationStore.registrateUser(registrationData.value)

        if(!isFirstTry.value) isFirstTry.value = true
    } else {
        window.smartCaptcha.reset(smartCaptchaId.value)
    }
}

if(route.query.verify) {
    verifyMode.value = true
    registrationStore.registrateVerify(route.query.verify)
}

watch(registrateUserMsg, (newMsg, oldMsg) => {

    if(oldMsg.success && newMsg == {}) {

        router.push('/auth')
    } 
})

</script>

<template>
    <div class="uta-registration">
        <div class="uta-registration__header">
            <img src="@/assets/images/logo.png" alt="" class="uta-registration__header-img">
        </div>
        <template v-if="!verifyMode">
            <form action="" @submit.prevent="submitRegistration" class="uta-registration__form">
                <div class="mb-4 uta-alert uta-alert--danger" v-if="scriptErrors.length">
                    <span class="material-icons uta-alert__icon">error_outline</span>
                    <div class="uta-alert__text" v-for="(error, index) in scriptErrors" :key="error">
                        {{ error }}{{ index !== scriptErrors.length - 1 ? ', ' : '' }} 
                    </div>
                </div>
                <div class="mb-4 uta-alert uta-alert--danger" v-if="formErrors.length">
                    <span class="material-icons uta-alert__icon">error_outline</span>
                    <div class="uta-alert__text">
                        {{ getTranslate.EFormFields }}:
                        <span class="uta-alert__text-item" v-for="(error, index) in formErrors" :key="error">
                            {{ error }}{{ index !== formErrors.length - 1 ? ', ' : '' }} 
                        </span>
                    </div>
                    <button type="button" class="uta-alert__close" @click="formErrors = [], formInvalids = {}">
                        <span class="material-icons">
                            close
                        </span>
                    </button>
                </div>
                <div 
                    v-if="'success' in registrateUserMsg"
                    class="mb-4 uta-alert" 
                    :class="{ 
                        'uta-alert--success' : registrateUserMsg.success, 
                        'uta-alert--danger' : !registrateUserMsg.success 
                    }"
                > 
                    <span class="material-icons uta-alert__icon">
                        {{ registrateUserMsg.success ? 'check_circle' : 'error_outline' }}
                    </span>
                    <div class="uta-alert__text">
                        {{ getTranslate[registrateUserMsg.text] || registrateUserMsg.text }}
                    </div>
                    <button type="button" class="uta-alert__close" @click="setRegistrateUserMsg({})">
                        <span class="material-icons">
                            close
                        </span>
                    </button>
                </div>
                <template v-if="!('success' in registrateUserMsg && registrateUserMsg.success)">
                    <input 
                        class="mb-3 uta-input uta-input--block uta-registration__input" 
                        :class="{ 'uta-input--invalid' : formInvalids.UTARegistrationLastname }"
                        type="text" 
                        id="UTARegistrationLastname" 
                        autocomplete="off"
                        :placeholder="getTranslate.REGISTRATION_LASTNAME"
                        @input="delete formInvalids.UTARegistrationLastname"
                        v-model="UTARegistrationLastname"
                    >
                    <input 
                        class="mb-3 uta-input uta-input--block uta-registration__input" 
                        :class="{ 'uta-input--invalid' : formInvalids.UTARegistrationName }"
                        type="text" 
                        id="UTARegistrationName" 
                        autocomplete="off"
                        :placeholder="getTranslate.REGISTRATION_NAME"
                        @input="delete formInvalids.UTARegistrationName"
                        v-model="UTARegistrationName"
                    >
                    <input 
                        class="mb-3 uta-input uta-input--block uta-registration__input" 
                        :class="{ 'uta-input--invalid' : formInvalids.UTARegistrationSurname }"
                        type="text" 
                        id="UTARegistrationSurname" 
                        autocomplete="off"
                        :placeholder="getTranslate.REGISTRATION_SURNAME"
                        @input="delete formInvalids.UTARegistrationSurname"
                        v-model="UTARegistrationSurname"
                    >
                    <input 
                        class="mb-3 uta-input uta-input--block uta-registration__input" 
                        :class="{ 'uta-input--invalid' : formInvalids.UTARegistrationEmail }"
                        type="text" 
                        id="UTARegistrationEmail" 
                        autocomplete="off"
                        :placeholder="getTranslate.REGISTRATION_LOGIN"
                        @input="delete formInvalids.UTARegistrationEmail"
                        v-model="UTARegistrationEmail"
                    >
                    <div class="mb-3 input-group uta-input__wrapper uta-search">
                        <button class="btn dropdown-toggle uta-registration__input-dropdown-btn" type="button" data-bs-toggle="dropdown" aria-expanded="false">{{ UTARegistrationPhoneCode }}</button>
                        <ul class="dropdown-menu">
                            <li><a class="dropdown-item" href="#" @click="UTARegistrationPhoneCode = '+7'">Россия</a></li>
                            <li><a class="dropdown-item" href="#" @click="UTARegistrationPhoneCode = '+7'">Казахстан</a></li>
                            <li><a class="dropdown-item" href="#" @click="UTARegistrationPhoneCode = '+375'">Беларусь</a></li>
                            <li><a class="dropdown-item" href="#" @click="UTARegistrationPhoneCode = '+998'">Узбекистан</a></li>
                            <li><a class="dropdown-item" href="#" @click="UTARegistrationPhoneCode = '+992'">Таджикистан</a></li>
                            <li><a class="dropdown-item" href="#" @click="UTARegistrationPhoneCode = '+374'">Армения</a></li>
                        </ul>
                        <input 
                            type="text" 
                            class="uta-input uta-input--block uta-input__wrapper-input uta-registration__input" 
                            :class="{ 'uta-input--invalid' : formInvalids.UTARegistrationPhone }"
                            id="UTARegistrationPhone" 
                            autocomplete="off"
                            :placeholder="getTranslate.REGISTRATION_PHONE"
                            @input="delete formInvalids.UTARegistrationPhone"
                            v-model="UTARegistrationPhone"
                        >
                    </div>
<!-- 


                    <div class="uta-input-group">
                        <input class="uta-input-group__input" type="text">
                        <span class="uta-input-group__icon"></span>
                    </div>

 -->

                    <div class="mb-3 uta-input__wrapper uta-password">
                        <input 
                            autocomplete="off"
                            :type="showPassword ? 'text' : 'password'" 
                            class="uta-input uta-input--block uta-input__wrapper-input" 
                            :class="{ 'uta-input--invalid' : formInvalids.UTARegistrationPass }"
                            id="UTARegistrationPass"
                            :placeholder="getTranslate.REGISTRATION_PASS"
                            @input="delete formInvalids.UTARegistrationPass"
                            v-model="UTARegistrationPass"
                        >
                        <span 
                            class="material-icons uta-input__wrapper-icon"
                            @click="showPassword = !showPassword"
                        >
                            {{ showPassword ? 'visibility' : 'visibility_off' }} 
                        </span>
                    </div>
                    <UTAPassIndicator :password="UTARegistrationPass" />
                    <div class="uta-agree uta-agree--personal">
                        <input 
                            type="checkbox" 
                            class="uta-agree__input" 
                            id="UTARegistrationAgree"
                            v-model="UTARegistrationAgree"
                            @change="UTARegistrationAgree ? delete formInvalids.UTAAuthAgree : null"
                        >
                        <label for="UTARegistrationAgree" class="uta-agree__label">
                            <div 
                                class="uta-agree__label-check"
                                :class="{'uta-agree__label-check--invalid' : formInvalids.UTAAuthAgree }"
                            >
                                <span 
                                    class="material-symbols-outlined uta-agree__label-check-arrow" 
                                    :class="{ 
                                        'visible' : UTARegistrationAgree, 
                                        'invisible'  : !UTARegistrationAgree
                                    }"
                                >
                                    check
                                </span>
                            </div>
                            <div class="uta-agree__label-text">
                                {{ getTranslate.AGREE_PERSONAL_TEXT }}
                                <a :href="personalDataAgree" target="_blank" class="uta-agree__label-text-link">{{ getTranslate.AGREE_PERSONAL_LINK }}</a>
                            </div>
                        </label>
                    </div>
                    <button type="submit" class="uta-btn uta-btn--main uta-btn--block uta-btn--md uta-registration__submit">
                        <template v-if="!registrateUserProcess">
                            {{ getTranslate.REGISTRATION_SUBMIT }}
                        </template>
                        <div class="loader" v-if="registrateUserProcess">
                            <div class="spinner-border text-secondary" role="status">
                                <span class="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    </button>
                    <UTASmartCaptcha @getToken="captchaValidate" @error="setScriptError" />
                </template>
                <div class="uta-auth__bottom">
                    <RouterLink to="/auth" class="uta-auth__bottom-link uta-auth__bottom-link">{{ getTranslate.AUTH }}</RouterLink>
                    <RouterLink to="/forgot" class="uta-auth__bottom-link uta-auth__bottom-link">{{ getTranslate.AUTH_FORGOT }}</RouterLink>
                </div>
            </form>
        </template>
        <template v-else>
            <div 
                v-if="'success' in registrateVerifyMsg"
                class="uta-alert uta-registration__validate-alert" 
                :class="{
                    'uta-alert--success' : registrateVerifyMsg.success, 
                    'uta-alert--danger' : !registrateVerifyMsg.success
                }"
            >
                <div class="uta-alert__text">
                    {{ getTranslate[registrateVerifyMsg.text] || registrateVerifyMsg.text }}
                    <router-link to="/auth" class="uta-btn uta-btn--main uta-btn--block mt-3 uta-registration__validate-link" v-if="registrateVerifyMsg.success">
                        {{ getTranslate.AUTH }}
                    </router-link>
                </div>
            </div>
            <div class="spinner-border uta-header__spinner uta-registration__validate-spinner" role="status" v-if="registrateVerifyProcess">
                <span class="visually-hidden">Loading...</span>
            </div>
        </template>
    </div>
</template>

<style lang="scss">
@import '@/assets/styles/scss/_agree';
@import '@/assets/styles/scss/components/registration';
</style>